import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import './Integrations.css';
import './IntegrationsV2.css';
import jiraLogo from '../assets/jira-logo.png';
import githubLogo from '../assets/pnggithub.png';
import gitlabLogo from '../assets/pnggitlab.png';
import bitbucketLogo from '../assets/bitbucket-seeklogo.png';

const IntegrationsV2 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const roleUpper = String(user?.role || '').toUpperCase();
  const isTrial = roleUpper === 'TRIAL';
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { type: 'jira' | 'github' | 'gitlab', config: {} }

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('integrations'));
      if (response.data.success) {
        setConfigs(response.data.data || {});
      }
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      showError('Falha ao carregar integrações');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = (type) => {
    if (isTrial) {
      showError('Perfil TRIAL possui acesso somente leitura em Integrações.');
      return;
    }

    if (type === 'github-app') {
      navigate('/projects?focus=github');
      return;
    }

    const currentConfig = configs[type] || {};
    setModal({ type, config: { ...currentConfig } });
  };

  const handleSaveConfig = async (formData) => {
    if (isTrial) {
      showError('Perfil TRIAL não pode alterar integrações.');
      return;
    }
    try {
      let url = '';
      let payload = {};

      if (modal.type === 'jira') {
        url = getApiUrl('integrations/jira/config');
        payload = {
          url: formData.url,
          repos: formData.repos,
          scope: 'user'
        };
      } else if (modal.type === 'jira-basic') {
        url = getApiUrl('integrations/jira-basic/config');
        payload = {
            url: formData.url,
            owner_email: formData.owner_email,
            email: formData.email,
            apiToken: formData.apiToken
        }
      } else {
          // Genérico para outros providers se houver
          url = getApiUrl(`integrations/${modal.type}/config`);
          payload = formData;
      }

      const response = await axios.post(url, payload);
      
      if (response.data.success) {
        showSuccess('Configuração salva com sucesso');
        setModal(null);
        fetchConfigs();
      } else {
        showError(response.data.reason || 'Erro ao salvar configuração');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      showError('Erro ao conectar com o servidor');
    }
  };



  if (loading) return <div className="loading">Carregando integrações...</div>;

  return (
    <div className="integrations-container">
      <header className="page-header">
        <h1>🔗 Integrações</h1>
        <p>Configure conexões com ferramentas externas para centralizar seu fluxo de QA.</p>
      </header>

      <div className="integrations-grid">
        {/* Card do Jira (Basic Auth) */}
        <div className={`integration-card ${configs['jira-basic']?.active ? 'active' : ''}`}>
          <div className="card-header">
             <div className="provider-logo-container" style={{ width: '110px' }}>
                 <img src={jiraLogo} alt="Jira Logo" />
             </div>
          </div>
          <p>Conecte-se ao Jira usando Email e API Token.</p>
          <div className="card-status">
            Status: <span className={configs['jira-basic']?.connected ? 'status-ok' : 'status-error'}>
              {configs['jira-basic']?.connected ? 'Conectado' : 'Não conectado'}
            </span>
          </div>
          <button className="btn-configure" onClick={() => handleConfigure('jira-basic')} disabled={isTrial} title={isTrial ? 'Usuário TRIAL não tem acesso para configurar integrações.' : 'Configurar integração'}>
            {isTrial ? 'Somente visualização' : 'Configurar'}
          </button>
        </div>

        {/* Card do GitHub */}
        <div className="integration-card active">
          <div className="card-header">
             <div className="provider-logo-container" style={{ width: '60px' }}>
                 <img src={githubLogo} alt="GitHub Logo" />
             </div>
          </div>
          <p>Conecte repositórios GitHub aos projetos para consultar branches, pull requests, issues e releases.</p>
          <div className="card-status">
            Status: <span className="status-ok">Disponível</span>
          </div>
          <button
            className="btn-configure"
            onClick={() => handleConfigure('github-app')}
            disabled={isTrial}
            title={isTrial ? 'Usuário TRIAL não tem acesso para configurar integrações.' : 'Configurar GitHub nos projetos'}
          >
            Configurar
          </button>
        </div>

        {/* Card do GitLab (Em Manutenção) */}
        <div className="integration-card dev-mode">
          <div className="card-header">
             <div className="provider-logo-container">
                 <img src={gitlabLogo} alt="GitLab Logo" />
             </div>
             <span className="badge-dev">Em Manutenção</span>
          </div>
          <p>Integração com pipelines e issues do GitLab.</p>
          <div className="card-status">
            Status: <span>Indisponível</span>
          </div>
          <button className="btn-configure" disabled>
            Configurar
          </button>
        </div>

        {/* Card do Bitbucket (Em Manutenção) */}
        <div className="integration-card dev-mode">
          <div className="card-header">
             <div className="provider-logo-container">
                 <img src={bitbucketLogo} alt="Bitbucket Logo" />
             </div>
             <span className="badge-dev">Em Manutenção</span>
          </div>
          <p>Integração com repositórios e pipelines do Bitbucket.</p>
          <div className="card-status">
            Status: <span>Indisponível</span>
          </div>
          <button className="btn-configure" disabled>
            Configurar
          </button>
        </div>

      </div>

      {modal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Configurar {modal.type === 'jira-basic' ? 'Jira' : modal.type}</h2>
            
            {modal.type === 'jira-basic' && (
                <ConfigFormJiraBasic 
                    initialData={modal.config} 
                    readOnly={isTrial}
                    onSave={handleSaveConfig} 
                    onCancel={() => setModal(null)} 
                />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ConfigFormJiraBasic = ({ initialData, onSave, onCancel, readOnly = false }) => {
    const [activeTab, setActiveTab] = useState('config'); // 'config' | 'logs'
    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [formData, setFormData] = useState({
        url: initialData.url || '',
        owner_email: initialData.owner_email || '', // Admin: Email do Dono do Token
        email: initialData.email || initialData.name || '', // User: Email do Usuário
        apiToken: '' // Sempre vazio por segurança
    });

    const isAdmin = !!initialData.is_admin;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const fetchLogs = async () => {
        setLoadingLogs(true);
        try {
            const res = await axios.get(getApiUrl('integrations/jira-basic/logs'));
            if (res.data.success) {
                setLogs(res.data.data || []);
            }
        } catch (error) {
            console.error('Erro ao buscar logs:', error);
        } finally {
            setLoadingLogs(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'logs') {
            fetchLogs();
        }
    }, [activeTab]);

    return (
        <div className="config-modal-container">
            <div className="tabs">
                <button 
                    className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
                    onClick={() => setActiveTab('config')}
                >
                    Configuração
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('logs')}
                >
                    Logs de Erro
                </button>
            </div>

            {activeTab === 'config' ? (
                <form onSubmit={handleSubmit}>
                    {isAdmin && (
                        <div className="admin-section" style={{ borderBottom: '1px solid #444', paddingBottom: '15px', marginBottom: '15px' }}>
                            <h4 style={{ color: '#aaa', marginBottom: '10px' }}>Configuração da Empresa (Admin/Proprietário)</h4>
                            <div className="form-group">
                                <label>URL do Jira (ex: https://suaempresa.atlassian.net)</label>
                                <input 
                                    type="text" 
                                    value={formData.url} 
                                    onChange={e => setFormData({...formData, url: e.target.value})}
                                    disabled={readOnly}
                                    required 
                                    placeholder="https://..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Email do Dono do Token (Gerente/Admin)</label>
                                <input 
                                    type="email" 
                                    value={formData.owner_email} 
                                    onChange={e => setFormData({...formData, owner_email: e.target.value})}
                                    disabled={readOnly}
                                    required 
                                    placeholder="admin@empresa.com"
                                />
                                <small>Este email deve corresponder ao usuário que gerou o API Token.</small>
                            </div>
                            <div className="form-group">
                                <label>API Token da Empresa {initialData.connected && '(Configurado)'}</label>
                                <input 
                                    type="password" 
                                    value={formData.apiToken} 
                                    onChange={e => setFormData({...formData, apiToken: e.target.value})}
                                    disabled={readOnly}
                                    placeholder={initialData.connected ? "•••••••• (Deixe em branco para manter)" : "Insira o token da empresa"}
                                />
                                <small>Gere um token em: <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer">Atlassian Security</a></small>
                            </div>
                        </div>
                    )}

                    <div className="user-section">
                        <h4 style={{ color: '#aaa', marginBottom: '10px' }}>Sua Configuração (Usuário)</h4>
                        {!isAdmin && (
                            <div className="form-group">
                                <label>Status da Integração da Empresa</label>
                                <div style={{ padding: '8px', background: initialData.connected ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)', borderRadius: '4px', color: initialData.connected ? '#4caf50' : '#f44336' }}>
                                    {initialData.connected ? '✅ Empresa Conectada' : '❌ Empresa Não Conectada (Contate o Admin)'}
                                </div>
                            </div>
                        )}
                        <div className="form-group">
                            <label>Seu Email no Jira</label>
                            <input 
                                type="email" 
                                value={formData.email} 
                                onChange={e => setFormData({...formData, email: e.target.value})}
                                disabled={readOnly}
                                required 
                                placeholder="seu.nome@empresa.com"
                            />
                            <small>Email usado para identificar suas ações no Jira (bugs, subtasks, comentários, etc).</small>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={onCancel} className="btn-cancel">Cancelar</button>
                        {!readOnly && <button type="submit" className="btn-save">Salvar</button>}
                    </div>
                </form>
            ) : (
                <div className="logs-container">
                    <div className="logs-header">
                        <h3>Últimas atividades</h3>
                        <button onClick={fetchLogs} className="btn-refresh" title="Atualizar">🔄</button>
                    </div>
                    {loadingLogs ? (
                        <div className="loading-logs">Carregando logs...</div>
                    ) : logs.length === 0 ? (
                        <div className="empty-logs">Nenhum log encontrado.</div>
                    ) : (
                        <div className="logs-list">
                            {logs.map(log => (
                                <div key={log.id} className={`log-item ${log.status}`}>
                                    <div className="log-top">
                                        <span className="log-status">{log.status === 'success' ? '✅' : '❌'}</span>
                                        <span className="log-type">{log.type}</span>
                                        <span className="log-date">{new Date(log.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="log-message">{log.message}</div>
                                    {log.payload && (
                                        <pre className="log-payload">
                                            {JSON.stringify(typeof log.payload === 'string' ? JSON.parse(log.payload) : log.payload, null, 2)}
                                        </pre>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="modal-actions">
                        <button type="button" onClick={onCancel} className="btn-cancel">Fechar</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntegrationsV2;
